import React, { useState, useEffect, useCallback, useRef } from 'react'; 
import {
  Box,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import Loading from '../../components/Loading';
import DiaryActivitiesTable from '../../components/DiaryActivitiesTable';
import UserSelect from '../../components/UserSelect';
import { useAuth } from '../../contexts/auth';
import { log } from '../../utils/logger';

const DiaryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlId } = useParams();  
  const { enqueueSnackbar } = useSnackbar();
  const { user, signed, loading: authLoading, validateToken } = useAuth();
  const addActivityButtonRef = useRef(null);
  const importantInfoFieldRef = useRef(null);

  // Estados
  const [loading, setLoading] = useState(true);
  const [publishingDiary, setPublishingDiary] = useState(false);
  const [savingDiary, setSavingDiary] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [diaryId, setDiaryId] = useState(urlId);
  const [diary, setDiary] = useState({
    lar_id: '',
    professional_id: '',
    resident_id: '',
    activities: []
  });
  const [lares, setLares] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [residents, setResidents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [intensities, setIntensities] = useState([]);
  const [diaryActivities, setDiaryActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityId, setActivityId] = useState('');
  const [intensityId, setIntensityId] = useState('');
  const [importantInfo, setImportantInfo] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [refreshingAI, setRefreshingAI] = useState(false);
  const [dataLoadError, setDataLoadError] = useState(null);

  // Verificar se o usuário tem permissão para editar o diário
  // Função comentada pois não está sendo usada atualmente, mas mantida para referência futura
  // const canEditDiary = useCallback((diaryData) => {
  //   if (!diaryData) return true; // Se não há diário, estamos criando um novo
  //   return user?.profile === 'Administrador' || diaryData.status === 'Draft';
  // }, [user]);

  // Callbacks
  const findNormalIntensity = useCallback((intensitiesList) => {
    const normalIntensity = intensitiesList.find(i => i.name.toLowerCase() === 'normal');
    return normalIntensity ? normalIntensity.id : '';
  }, []);

  const fetchProfessionalsAndResidents = useCallback(async (larId) => {
    try {
      const [professionalsResponse, residentsResponse] = await Promise.all([
        api.get(`/api/users/professionals/${larId}`),
        api.get(`/api/users/residents/${larId}`)
      ]);
      
      const professionalsData = professionalsResponse.data.map(user => ({
        id: user.id,
        name: user.name,
        nickname: user.nickname,
        photo: user.photo,
        profile: user.profile
      }));
      
      const residentsData = residentsResponse.data.map(user => ({
        id: user.id,
        name: user.name,
        nickname: user.nickname,
        photo: user.photo,
        profile: user.profile
      }));
      
      setProfessionals(professionalsData);
      setResidents(residentsData);
      return true;
    } catch (error) {
      log('Error fetching users', error.response || error, 'error');
      
      if (error.response?.status === 401) {
        const isValid = await validateToken();
        if (!isValid) {
          enqueueSnackbar('Sessão expirada. Redirecionando para login...', { variant: 'error' });
          return false;
        }
      } else {
        enqueueSnackbar('Erro ao carregar usuários', { variant: 'error' });
      }
      return false;
    }
  }, [enqueueSnackbar, validateToken]);

  // Effects
  useEffect(() => {
    if (authLoading || !signed) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setDataLoadError(null);
        
        const [
          laresResponse,
          activitiesResponse,
          intensitiesResponse
        ] = await Promise.all([
          api.get('/api/lares'),
          api.get('/api/activities', { params: { active: 'true' } }),
          api.get('/api/intensity')
        ]);

        const laresData = laresResponse.data;
        log('Lares carregados', laresData, 'info');
        
        // Garantir que laresData seja sempre um array
        const laresArray = Array.isArray(laresData) ? laresData : [];
        setLares(laresArray);

        if (!diaryId && laresArray.length > 0) {
          const firstLar = laresArray[0];
          setDiary(prev => ({ ...prev, lar_id: firstLar.id }));
          await fetchProfessionalsAndResidents(firstLar.id);
        }

        setActivities(activitiesResponse.data);
        setIntensities(intensitiesResponse.data);
        setIntensityId(findNormalIntensity(intensitiesResponse.data));

        if (diaryId) {
          const diaryResponse = await api.get(`/api/diary/${diaryId}`);
          log('Diário carregado:', diaryResponse.data);
          
          // Verificar se o lar_id do diário existe na lista de lares
          const diaryLarId = diaryResponse.data.lar_id;
          
          // Verificar se o lar do diário existe na lista de lares
          const larExists = Array.isArray(laresArray) && diaryLarId ? 
            laresArray.some(lar => lar.id === diaryLarId) : false;
          
          if (!larExists && diaryLarId) {
            log(`Lar ID ${diaryLarId} não encontrado na lista de lares disponíveis`);
            // Tentar buscar o lar específico
            try {
              const specificLarResponse = await api.get(`/api/lares/${diaryLarId}`);
              if (specificLarResponse.data) {
                log('Lar específico carregado:', specificLarResponse.data);
                // Adicionar o lar à lista se ele existir
                setLares(prev => {
                  const prevArray = Array.isArray(prev) ? prev : [];
                  return [...prevArray, specificLarResponse.data];
                });
              }
            } catch (larError) {
              log('Erro ao buscar lar específico:', larError);
            }
          }
          
          setDiary(diaryResponse.data);
          setDiaryActivities(diaryResponse.data.activities || []);
          await fetchProfessionalsAndResidents(diaryResponse.data.lar_id);
          
          // Redirecionar para visualização se o diário estiver publicado
          if (diaryResponse.data.status === 'Published' && location.pathname.includes('/edit')) {
            log('Diário publicado não pode ser editado. Redirecionando para visualização.');
            enqueueSnackbar('Diários publicados não podem ser editados.', { variant: 'warning' });
            navigate(`/diary/${diaryId}`);
            return;
          }
        }

        setLoading(false);
      } catch (error) {
        log('Error fetching data:', error);
        
        if (error.response?.status === 401) {
          const isValid = await validateToken();
          if (!isValid) {
            setDataLoadError('Sessão expirada. Por favor, faça login novamente.');
          }
        } else {
          setDataLoadError('Erro ao carregar dados. Por favor, tente novamente.');
          enqueueSnackbar('Erro ao carregar dados', { variant: 'error' });
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [diaryId, fetchProfessionalsAndResidents, enqueueSnackbar, findNormalIntensity, signed, authLoading, validateToken, location.pathname, navigate]);

  useEffect(() => {
    if (diaryId) {
      setHasChanges(false);
    }
  }, [diaryId, diaryActivities]);

  // Event Handlers
  const handleLarChange = async (event) => {
    const newLarId = event.target.value;
    setDiary(prev => ({ ...prev, lar_id: newLarId }));
    setHasChanges(true);
    await fetchProfessionalsAndResidents(newLarId);
  };

  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setActivityId(activity.activity_id);
    setIntensityId(activity.intensity_id);
    setImportantInfo(activity.important_info || '');
  };

  const handleDeleteActivity = async (activity) => {
    setActivityToDelete(activity);
  };

  const confirmDeleteActivity = async () => {
    if (!activityToDelete) return;

    try {
      const response = await api.delete(`/api/diary/${diaryId}/activities/${activityToDelete.id}`);
      
      setDiaryActivities(prevActivities => 
        prevActivities.filter(a => a.id !== activityToDelete.id)
      );

      if (response.data?.diary) {
        setDiary(response.data.diary);
      }

      enqueueSnackbar('Atividade removida com sucesso', { variant: 'success' });
      setHasChanges(true);
    } catch (error) {
      log('Error deleting activity:', error);
      
      if (error.response?.status === 401) {
        const isValid = await validateToken();
        if (!isValid) {
          enqueueSnackbar('Sessão expirada. Redirecionando para login...', { variant: 'error' });
          return;
        }
      }
      
      enqueueSnackbar('Erro ao remover atividade', { variant: 'error' });
    } finally {
      setActivityToDelete(null);
    }
  };

  const handleAddActivity = async () => {
    if (!activityId || intensityId === undefined || intensityId === null || intensityId === '') {
      enqueueSnackbar('Por favor, selecione a atividade e a intensidade', { variant: 'warning' });
      return;
    }

    try {
      let currentDiaryId = diaryId;
      
      if (!currentDiaryId) {
        const diaryData = {
          lar_id: diary.lar_id,
          professional_id: diary.professional_id,
          resident_id: diary.resident_id
        };

        const response = await api.post('/api/diary', diaryData);
        currentDiaryId = response.data.id;
        setDiary(response.data);
        setDiaryId(currentDiaryId);
        window.history.replaceState(null, '', `/diary/${currentDiaryId}/edit`);
        enqueueSnackbar('Diário criado com sucesso', { variant: 'success' });
      }

      // Certifica-se de obter o valor mais recente do campo
      const currentImportantInfo = importantInfoFieldRef.current?.value || importantInfo;

      if (selectedActivity) {
        const response = await api.put(`/api/diary/${currentDiaryId}/activities/${selectedActivity.id}`, {
          intensity_id: intensityId,
          important_info: currentImportantInfo
        });

        if (response.data?.activity) {
          setDiaryActivities(prevActivities => 
            prevActivities.map(activity => 
              activity.id === selectedActivity.id
                ? response.data.activity
                : activity
            )
          );

          if (response.data.diary) {
            setDiary(response.data.diary);
          }

          enqueueSnackbar('Atividade atualizada com sucesso', { variant: 'success' });
        }
      } else {
        const response = await api.post(`/api/diary/${currentDiaryId}/activities`, {
          activity_id: activityId,
          intensity_id: intensityId,
          important_info: currentImportantInfo
        });

        if (response.data?.activity) {
          setDiaryActivities(prev => [...prev, response.data.activity]);

          if (response.data.diary) {
            setDiary(response.data.diary);
          }

          enqueueSnackbar('Atividade adicionada com sucesso', { variant: 'success' });
        }
      }

      // Limpa o formulário mantendo a intensidade "Normal"
      setSelectedActivity(null);
      setActivityId('');
      setIntensityId('');
      setImportantInfo('');
      importantInfoFieldRef.current.value = ''; // Limpa o campo de texto
      const normalIntensityId = findNormalIntensity(intensities);
      setIntensityId(normalIntensityId);
    } catch (error) {
      log('Error adding/updating activity:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Erro ao adicionar/atualizar atividade', 
        { variant: 'error' }
      );
    }
  };

  const handleSaveDiary = async () => {
    // Validar campos obrigatórios
    if (!diary.lar_id) {
      enqueueSnackbar('Selecione um lar', { variant: 'error' });
      return;
    }
    if (!diary.professional_id) {
      enqueueSnackbar('Selecione um profissional', { variant: 'error' });
      return;
    }
    if (!diary.resident_id) {
      enqueueSnackbar('Selecione um residente', { variant: 'error' });
      return;
    }

    try {
      setSavingDiary(true);
      const data = {
        lar_id: diary.lar_id,
        professional_id: diary.professional_id,
        resident_id: diary.resident_id
      };

      if (diaryId) {
        await api.put(`/api/diary/${diaryId}`, data);
        enqueueSnackbar('Diário atualizado com sucesso', { variant: 'success' });
      } else {
        const response = await api.post('/api/diary', data);
        setDiary(response.data);
        setDiaryId(response.data.id);
        window.history.replaceState(null, '', `/diary/${response.data.id}/edit`);
        enqueueSnackbar('Diário criado com sucesso', { variant: 'success' });
      }

      setHasChanges(false);
    } catch (error) {
      log('Error saving diary:', error);
      enqueueSnackbar('Erro ao salvar diário: ' + (error.response?.data?.error || error.message), { variant: 'error' });
    } finally {
      setSavingDiary(false);
    }
  };

  // Renderização condicional
  if (authLoading) {
    return <Loading message="Verificando autenticação..." />;
  }

  if (!signed) {
    return <Loading message="Redirecionando..." />;
  }

  if (dataLoadError) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="error" gutterBottom>
          {dataLoadError}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/diary')}
        >
          Voltar para a lista de diários
        </Button>
      </Box>
    );
  }

  if (loading) {
    return <Loading message="Carregando dados..." />;
  }

  // Verificar se o diário pode ser editado
  if (diaryId && diary?.status === 'Published' && location.pathname.includes('/edit')) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="warning.main" gutterBottom>
          Diários publicados não podem ser editados
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/diary/${diaryId}`)}
          sx={{ mr: 2 }}
        >
          Visualizar Diário
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/diary')}
        >
          Voltar para a lista
        </Button>
      </Box>
    );
  }

  // Renderização principal
  return (
    <Box>
      <form onSubmit={(e) => {
        e.preventDefault();
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4">{diaryId ? 'Editar' : 'Novo'} Diário</Typography>
            {diaryId && (
              <Typography variant="subtitle1" color="textSecondary">
                ID: {diaryId}
              </Typography>
            )}
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Lar</InputLabel>
                <Select
                  value={diary?.lar_id && Array.isArray(lares) && lares.some(lar => lar.id === diary.lar_id) ? diary.lar_id : ''}
                  onChange={handleLarChange}
                  label="Lar"
                  required
                >
                  {Array.isArray(lares) ? lares.map(lar => (
                    <MenuItem key={lar.id} value={lar.id}>
                      {lar.nome}
                    </MenuItem>
                  )) : null}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <UserSelect
                label="Profissional"
                value={diary?.professional_id || ''}
                onChange={(e) => {
                  setDiary(prev => ({ ...prev, professional_id: e.target.value }));
                  setHasChanges(true);
                }}
                options={professionals}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <UserSelect
                label="Residente"
                value={diary?.resident_id || ''}
                onChange={(e) => {
                  const newResidentId = e.target.value;
                  setDiary(prev => ({ ...prev, resident_id: newResidentId }));
                  setHasChanges(true);
                  
                  // Salvar automaticamente o diário após a seleção do residente
                  if (newResidentId && diary.lar_id && diary.professional_id) {
                    // Timeout para garantir que o estado foi atualizado antes de salvar
                    setTimeout(() => {
                      const saveData = async () => {
                        try {
                          setSavingDiary(true);
                          const data = {
                            lar_id: diary.lar_id,
                            professional_id: diary.professional_id,
                            resident_id: newResidentId
                          };

                          if (diaryId) {
                            await api.put(`/api/diary/${diaryId}`, data);
                            enqueueSnackbar('Diário atualizado com sucesso', { variant: 'success' });
                          } else {
                            const response = await api.post('/api/diary', data);
                            setDiary(response.data);
                            setDiaryId(response.data.id);
                            window.history.replaceState(null, '', `/diary/${response.data.id}/edit`);
                            enqueueSnackbar('Diário criado com sucesso', { variant: 'success' });
                          }

                          setHasChanges(false);
                        } catch (error) {
                          log('Error saving diary:', error);
                          enqueueSnackbar('Erro ao salvar diário: ' + (error.response?.data?.error || error.message), { variant: 'error' });
                        } finally {
                          setSavingDiary(false);
                        }
                      };
                      saveData();
                    }, 100);
                  }
                }}
                options={residents}
                required
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Adicionar Atividade
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr 2fr auto" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Atividade</InputLabel>
              <Select
                value={activityId}
                onChange={(e) => setActivityId(e.target.value)}
                label="Atividade"
                required
              >
                {activities.map(activity => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Intensidade</InputLabel>
              <Select
                value={intensityId}
                onChange={(e) => setIntensityId(e.target.value)}
                label="Intensidade"
                required
              >
                {intensities.map(intensity => (
                  <MenuItem key={intensity.id} value={intensity.id}>
                    {intensity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Informações Importantes"
              key={selectedActivity ? `activity-${selectedActivity.id}` : 'new-activity'}
              defaultValue={selectedActivity ? selectedActivity.important_info || '' : ''}
              inputRef={importantInfoFieldRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (activityId) {
                    // Capturar o valor diretamente do campo
                    const value = e.target.value;
                    setImportantInfo(value);
                    // Usar setTimeout para garantir que o estado seja atualizado antes de chamar handleAddActivity
                    setTimeout(() => {
                      handleAddActivity();
                    }, 0);
                  } else {
                    enqueueSnackbar('Por favor, selecione uma atividade antes de pressionar Enter', { variant: 'warning' });
                  }
                }
              }}
              onBlur={(e) => setImportantInfo(e.target.value)}
              fullWidth
              name="important-info"
            />

            <Button
              ref={addActivityButtonRef}
              variant="contained"
              onClick={handleAddActivity}
              startIcon={selectedActivity ? <EditIcon /> : <AddIcon />}
              disabled={!activityId}
            >
              {selectedActivity ? 'Atualizar Atividade' : 'Adicionar Atividade'}
            </Button>
            {selectedActivity && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedActivity(null);
                  setActivityId('');
                  setIntensityId('');
                  setImportantInfo('');
                }}
                sx={{ ml: 1 }}
              >
                Cancelar Edição
              </Button>
            )}
          </Box>
        </Paper>

        <DiaryActivitiesTable 
          activities={diaryActivities}
          onEdit={handleEditActivity}
          onDelete={handleDeleteActivity}
          isEditing={true}
        />

        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ai-info-content"
            id="ai-info-header"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography variant="h6">Informações de IA</Typography>
              <Tooltip title="Atualizar informações de IA">
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation(); // Evita que o accordion abra/feche ao clicar no botão
                    const handleRefreshAI = async () => {
                      try {
                        setRefreshingAI(true);
                        const response = await api.get(`/api/diary/${diaryId}/ai-info`);
                        setDiary(prev => ({
                          ...prev,
                          ai_mood: response.data.ai_mood,
                          ai_health: response.data.ai_health,
                          ai_sociability: response.data.ai_sociability,
                          ai_comments: response.data.ai_comments
                        }));
                        enqueueSnackbar('Informações de IA atualizadas com sucesso', { variant: 'success' });
                      } catch (error) {
                        log('Erro ao atualizar informações de IA:', error);
                        enqueueSnackbar('Erro ao atualizar informações de IA', { variant: 'error' });
                      } finally {
                        setRefreshingAI(false);
                      }
                    };
                    handleRefreshAI();
                  }} 
                  disabled={refreshingAI || !diaryId}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Texto-base pré-IA do Diário"
                  value={diary.ai_summary_text_to_chatgpt || ''}
                  fullWidth
                  multiline
                  rows={5}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Resumo do Diário para o contato responsável pelo(a) residente"
                  value={diary.ai_summary_text_from_chatgpt || ''}
                  fullWidth
                  multiline
                  rows={5}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Status de envio do Diário"
                  value={diary.ai_summary_sent_log || ''}
                  fullWidth
                  multiline
                  rows={5}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texto do Diário criado por IA enviado em"
                  value={diary.ai_summary_sent_datetime || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {diaryId && (
          <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography variant="h6">Informações do Diário</Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Criado por
                </Typography>
                <Typography variant="body2">
                  {diary.creator?.name}
                  {diary.date_created && (
                    <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                      em {new Date(diary.date_created).toLocaleString('pt-BR')}
                    </Typography>
                  )}
                </Typography>
              </Box>

              {diary.updated_by && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Atualizado por
                  </Typography>
                  <Typography variant="body2">
                    {diary.updater?.name}
                    {diary.date_updated && (
                      <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                        em {new Date(diary.date_updated).toLocaleString('pt-BR')}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}

              {diary.published_by && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Publicado por
                  </Typography>
                  <Typography variant="body2">
                    {diary.publisher?.name}
                    {diary.date_published && (
                      <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                        em {new Date(diary.date_published).toLocaleString('pt-BR')}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button 
            onClick={() => {
              if (hasChanges) {
                setConfirmLeave(true);
              } else {
                navigate('/diary');
              }
            }}
            variant="outlined"
          >
            Fechar
          </Button>
          {(hasChanges || (!diary.lar_id || !diary.professional_id || !diary.resident_id)) && (
            <Button
              onClick={handleSaveDiary}
              variant="contained"
              color="primary"
              disabled={savingDiary}
            >
              {savingDiary ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
          {diaryId && diary?.status !== 'Published' && (
            <Button
              onClick={() => setConfirmPublishOpen(true)}
              variant="contained"
              color="secondary"
              sx={{ 
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                color: 'white',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
              }}
              disabled={publishingDiary}
            >
              Publicar
            </Button>
          )}
        </Box>

        {/* Diálogo de progresso durante a publicação */}
        <Dialog
          open={publishingDiary}
          onClose={() => {}}
          disableEscapeKeyDown
          PaperProps={{
            style: {
              backgroundColor: 'white',
              boxShadow: 'none',
              padding: '20px',
              minWidth: '300px',
              textAlign: 'center'
            },
          }}
        >
          <DialogTitle>Publicando Diário</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" py={2}>
              <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
              <DialogContentText>
                Por favor, aguarde enquanto o diário é publicado...
              </DialogContentText>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Diálogo de confirmação de publicação */}
        <Dialog
          open={confirmPublishOpen}
          onClose={() => setConfirmPublishOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: 'bold', color: '#f44336' }}>Atenção!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              As informações do diário foram revisadas e estão todas corretas? Diários publicados não podem ser alterados! Confirma a publicação?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmPublishOpen(false)} color="primary" variant="outlined">
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                try {
                  if (hasChanges) {
                    const data = {
                      lar_id: diary.lar_id,
                      professional_id: diary.professional_id,
                      resident_id: diary.resident_id
                    };
                    
                    await api.put(`/api/diary/${diaryId}`, data);
                    setHasChanges(false);
                  }
                  
                  setPublishingDiary(true);
                  await api.patch(`/api/diary/${diaryId}/status`, { status: 'Published' });
                  enqueueSnackbar('Diário publicado com sucesso', { variant: 'success' });
                  const response = await api.get(`/api/diary/${diaryId}`);
                  setDiary(response.data);
                } catch (error) {
                  log('Error publishing diary:', error);
                  
                  if (error.response?.status === 401) {
                    const isValid = await validateToken();
                    if (!isValid) {
                      enqueueSnackbar('Sessão expirada. Redirecionando para o login...', { variant: 'error' });
                      return;
                    }
                  }
                  
                  enqueueSnackbar('Erro ao publicar o diário', { variant: 'error' });
                } finally {
                  setPublishingDiary(false);
                  setConfirmPublishOpen(false);
                }
              }} 
              color="primary" 
              variant="contained"
              disabled={publishingDiary}
            >
              {publishingDiary ? 'Processando...' : 'Prosseguir'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação ao sair com alterações não salvas */}
        <Dialog
          open={confirmLeave}
          onClose={() => setConfirmLeave(false)}
        >
          <DialogTitle>Alterações não salvas</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Você tem alterações não salvas. Deseja realmente sair sem salvar?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmLeave(false)} color="primary">
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                setConfirmLeave(false);
                navigate('/diary');
              }} 
              color="primary" 
              autoFocus
            >
              Sair sem salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação para excluir atividade */}
        <Dialog
          open={!!activityToDelete}
          onClose={() => setActivityToDelete(null)}
        >
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deseja realmente excluir esta atividade?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivityToDelete(null)} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDeleteActivity} color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Box>
  );
};

export default DiaryForm;
